import { Formik } from "formik";

import { DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { type CoreCurrency } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";

import {
  FORM_FIELD_NAMES,
  MODAL_OPTION_VALUES,
  type ModalFormValues,
  type ModalType,
} from "./constants/modalConstants";
import { useModalValidation } from "./hooks/useModalValidation";
import { usePresetOptions } from "./hooks/usePresetOptions";
import { AddMetricSectionForm } from "./AddMetricSectionForm";

type AddItemParams = {
  modalType: ModalType;
  name: string;
  value: string;
  metricType?: DealMetricType;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  modalType: ModalType;
  onAdd: (params: AddItemParams) => boolean;
  existingNames: { name: string; metricType?: string }[];
  dealCurrency?: CoreCurrency;
};

export const AddMetricSectionModal = ({
  isOpen,
  onClose,
  modalType,
  onAdd,
  existingNames,
  dealCurrency,
}: Props) => {
  const { options } = usePresetOptions(modalType, isOpen, existingNames);
  const { validationSchema } = useModalValidation(modalType, existingNames);

  const initialValues: ModalFormValues = {
    [FORM_FIELD_NAMES.SELECTED_OPTION]: "",
    [FORM_FIELD_NAMES.CUSTOM_LABEL]: "",
    [FORM_FIELD_NAMES.CUSTOM_VALUE]: "",
    [FORM_FIELD_NAMES.METRIC_TYPE]: undefined,
  };

  const handleSubmit = (
    values: ModalFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    const selectedOption = values[FORM_FIELD_NAMES.SELECTED_OPTION];
    const customLabel = values[FORM_FIELD_NAMES.CUSTOM_LABEL];
    const customValue = values[FORM_FIELD_NAMES.CUSTOM_VALUE];
    const metricType = values[FORM_FIELD_NAMES.METRIC_TYPE];

    const label =
      selectedOption === MODAL_OPTION_VALUES.CUSTOM
        ? customLabel
        : selectedOption;

    const value =
      metricType === DealMetricType.MONEY && typeof customValue === "object"
        ? customValue.amount
        : (customValue as string);

    const success = onAdd({
      modalType,
      name: label,
      value,
      metricType,
    });

    if (success) {
      resetForm();
      onClose();
    }
  };

  return (
    <ActionaryModal
      fluidHeight
      open={isOpen}
      setOpen={(open) => !open && onClose()}
      title={m.coInvestments_lpDealOverviewContent_add({ type: modalType })}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <AddMetricSectionForm
          dealCurrency={dealCurrency}
          modalType={modalType}
          onClose={onClose}
          options={options}
        />
      </Formik>
    </ActionaryModal>
  );
};
